import { _, helpers, React, ts, ui } from '_core';

import { transform } from './helpers';
import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';
import { prepareBarChartBucketedNames, prepareBarChartCountNames } from '../../common/utils/get-series-name';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  fullScreen: boolean;
  params: Params;
  title: string;
};

const Chart = ({ id, readOnly, loadWidgetData, file, fullScreen, params, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
        },
      });

      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    const nestedBarData = nestedBarChartFormat(transform(data));
    return {
      data: prepareBarChartBucketedNames(
        nestedBarData.data,
        params.discrete ? ts.enums.BUCKET_TYPES_ENUM.FIXED_FLOAT : ts.enums.BUCKET_TYPES_ENUM.QUANTILES,
        params.quantiles
      ),
      count: prepareBarChartCountNames(
        nestedBarData.count,
        params.discrete ? ts.enums.BUCKET_TYPES_ENUM.FIXED_FLOAT : ts.enums.BUCKET_TYPES_ENUM.QUANTILES,
        params.quantiles
      ),
    };
  }, [data, params.discrete, params.quantiles]);

  const alert = error
    ? { type: 'error', message: helpers.parseApiError(error) }
    : data?.length === 0
      ? { type: 'warning', message: 'Empty File' }
      : undefined;

  return (
    <ui.NestedBarChart
      id={id}
      data={formattedData?.data}
      count={formattedData?.count}
      category="category"
      alert={alert as any}
      fullScreen={fullScreen}
      exportTitle={title}
      readOnly={readOnly}
    />
  );
};

export default Chart;
